import * as modal from "../util/modal.ts";
import * as Html from "../util/Html.ts";
import * as i18n from "../i18n.ts";
import ApiServer from "../ApiServer.ts";
import * as Storage from "../lib/Storage.ts";
import * as FwDatas from "../FwDatas.ts";
import * as Confetti from "../lib/Confetti.ts";
import { protoExtend } from "../lib/ObjectUtil.ts";
import TPL_PREMIUM from "../modal/premium.mustache";
import TPL_PAYWALL from "../modal/paywall.mustache";
import TPL_PROMO from "../modal/premium_promo.mustache";
import { format } from "../lib/DateUtils.ts";
import Icons from "../svg/Icons.ts";
import { CreditListCache } from "../server/objects/ICredit.ts";
import * as libLocation from "../lib/location.ts";
import * as segment from "../tracking-segment.ts";
import { CoverUriPhoneAndTabletBean, PremiumPopupConfigurableContentBean } from "../server/fizapilib.auto.ts";

// Augmentation of the generated beans with the properties and methods added dynamically below
declare module "../server/fizapilib.auto.ts" {
  interface CoverUriPhoneAndTabletBean {
    /** the tablet cover matching the color scheme of the browser */
    url?: string;
  }
  interface PremiumPopupConfigurableContentBean {
    /** a copy of the bean with every text rendered against the given values */
    configureText(values: Record<string, string>): PremiumPopupConfigurableContentBean;
  }
}

export function displayPromoPopupIfNeeded(){
  var premiumPopup = FwDatas.getState()?.premiumPopup;
  if(premiumPopup){
    var
      atLaunchResetDate = premiumPopup.atLaunchResetDate,
      fromStorage = Storage.localGet('atLaunchResetDate');


    if(fromStorage===null || new Date(fromStorage) < atLaunchResetDate){
      Storage.localSet('atLaunchResetDate',atLaunchResetDate.toISOString())
      Html.trigger(null,'takepremium',['directclick'])
    }
  }
}

CoverUriPhoneAndTabletBean.prototype.onDecode = function(this: CoverUriPhoneAndTabletBean): void {
  const isDarkModeActive = window.matchMedia('(prefers-color-scheme: dark)').matches;
  this.url = isDarkModeActive?this.tablet.darkMode:this.tablet.lightMode
}
PremiumPopupConfigurableContentBean.prototype.configureText = function(this: PremiumPopupConfigurableContentBean, values: Record<string, string>): PremiumPopupConfigurableContentBean {
  return protoExtend(this,{
    title:renderTemplate(this.title,values),
    description:renderTemplate(this.description,values),
    bannerTitle:renderTemplate(this.bannerTitle,values),
    bannerText:renderTemplate(this.bannerText,values),
  })
}

/**
 * The paywall of the AI import (FWWEB-1845): the yearly offer only, presented with the feature, or with the free
 * tries all spent when the circle had some
 */
async function aiImportPaywall(data :any, yearly :any, isKid :boolean){
  const
    premium = FwDatas.getState().premium,
    total = premium.aiImportByUpload==='KO_PREMIUM'?premium.permittedNbOfAIImportByUpload:undefined,
    TPL = await import("../modal/paywall_aiimport.mustache"),
    theModal = modal.openDomModal(TPL,{
      exhausted:!!total,
      exhaustedBody:total?i18n.get('aiimport.paywall.exhausted_body',{used:Math.min(total,premium.nbOfAIImportByUpload||0),total}):undefined,
      price:i18n.get(yearly.trialDays?'aiimport.paywall.price_trial':'aiimport.paywall.price',{
        monthly:data.yearlyByMonth, yearly:data.yearly, trial:yearly.trialDays
      }),
      buyUrl:yearly.buyUrl,
      cta:yearly.trialDays?i18n.get('premium_paywall.cta.button_trial',yearly.trialDays):i18n.get('actions.continue'),
      ctaSubtitle:yearly.trialDays?'premium_paywall.cta.subtitle_trial':'premium_paywall.cta.subtitle'
    });
  //the same events as the generic paywall
  let upgradeTapped = false;
  segment.track("Premium Screen Viewed",{source_screen:sourceScreen()});
  theModal.onClose = ()=>{
    if(!upgradeTapped) segment.track("Premium Screen Dismissed",{source_screen:sourceScreen()});
  };
  theModal.addEventListener('click',(evt)=>{
    if(!(evt.target as HTMLElement)?.closest('a.button')) return;
    if(isKid){
      evt.preventDefault();
      modalBlockedForKid();
      return;
    }
    upgradeTapped = true;
    segment.track("Premium Upgrade Tapped",{plan_type:"yearly",source_screen:sourceScreen()});
  });
}

/**
 * Logical name of the page a premium screen is opened from, for the source_screen property
 */
function sourceScreen(): string {
  return segment.getPageInfo(libLocation.getPageName()).page_name;
}

/**
 * Tracks the clicks on the links of a premium modal which are common to the paywall and the member card
 */
function trackPremiumLinks(target: HTMLElement){
  const href = target?.closest('a')?.getAttribute('href') || '';
  const legalLink = ['terms','privacy','sales'].find(type=>href.includes(type+'.html'));
  if(legalLink){
    segment.track("Premium Legal Link Tapped",{link_type:legalLink});
  }else if(href.startsWith('mailto:')){
    segment.track("Premium Support Tapped",{source_screen:sourceScreen()});
  }
}

function promopopup(data){
  segment.track("Premium Promo Offer Viewed",{source_screen:sourceScreen()});
  const promoModal = modal.openDomModal(TPL_PROMO,data);
  promoModal.addEventListener('click',(evt)=>{
    if((evt.target as HTMLElement)?.closest('a.button.premium[href]')){
      segment.track("Premium Upgrade Tapped",{plan_type:"yearly",source_screen:sourceScreen()});
    }
  })
}

Html.registerDom({
  async takepremium(from){
    let
      family = FwDatas.getFamily(),
      user = FwDatas.getUser(),
      isKid = user.isKid(),

      data :any = {
        directclick:from==='directclick'||from==='promo',
        blocked:user.isKid()
      },
      [activeCredits,twocheckoutUrls] = await Promise.all([CreditListCache.refresh(),ApiServer.credit2checkouturl2()]) ;
    if(activeCredits.length){
      //premium member card
      data.membercard = {
        avatars:family.members.slice(0,5).map((member,idx)=>{return{thumb:member.thumb(),left:24*idx}}),
        extra: Math.max(family.members.length-5,0),
        extraLeft : 24*5,
        date: format(activeCredits[0].creationDate,'MMMM YYYY')
      }
      data.nameOfPremiumProvider = activeCredits[0].author().name;
      Confetti.start(2000)
      const memberCard = modal.openDomModal(TPL_PREMIUM,data);
      memberCard.addEventListener('click',(evt)=>{
        const target = evt.target as HTMLElement;
        if(target?.closest('[data-fw-onclick*="settings_credits"]')){
          segment.track("Premium Subscription Manage Tapped");
        }
        trackPremiumLinks(target);
      })
      return;
    }

    //TODO handle promo
    data.yearly = formatPrice(twocheckoutUrls.yearly.currency,twocheckoutUrls.yearly.priceFloat);
    data.monthly = formatPrice(twocheckoutUrls.monthly.currency,twocheckoutUrls.monthly.priceFloat);
    data.yearlyByMonth = formatPrice(twocheckoutUrls.yearly.currency,twocheckoutUrls.yearly.priceFloat/12);
    data.monthlyByYear = formatPrice(twocheckoutUrls.monthly.currency,twocheckoutUrls.monthly.priceFloat*12);
    data.trialDays = twocheckoutUrls.monthly.trialDays;

    if(twocheckoutUrls.yearlyPromo){
      data.yearlyPromo = Object.assign(
        twocheckoutUrls.yearlyPromo,
        {
          price:formatPrice(twocheckoutUrls.yearlyPromo.currency,twocheckoutUrls.yearlyPromo.priceFloat -0.001),
          promoPercent:Math.round(twocheckoutUrls.yearlyPromo.percent*100)+'%'
        },
      );
		  var price = data.yearlyPromo.price;
      var percent = twocheckoutUrls.yearlyPromo.promoPercent = Math.round(twocheckoutUrls.yearlyPromo.percent*100)+"%"

      var serverContent = FwDatas.getState().premiumPopup?.content
      if(serverContent){
        data.content = serverContent.configureText({price,percentReduc:percent})
      }
    }
    if(twocheckoutUrls.monthlyPromo){
      data.monthlyPromo = Object.assign(
        twocheckoutUrls.monthlyPromo,
        {
          price:formatPrice(twocheckoutUrls.monthlyPromo.currency,twocheckoutUrls.monthlyPromo.priceFloat),
          promoPercent:Math.round(twocheckoutUrls.monthlyPromo.percent*100)+'%'
        }
      );
    }
    var waitingpremium = Storage.sessionGet('waitingpremium');
    if(waitingpremium){
      data.waitingpremium = (new Date().getTime() - new Date(waitingpremium).getTime() < 600000)
    }

    if(from==='aiimport'){
      aiImportPaywall(data, twocheckoutUrls.yearly, isKid);
      return;
    }
    var theModal = modal.openDomModal(TPL_PAYWALL,data);
    var upgradeTapped = false;
    segment.track("Premium Screen Viewed",{source_screen:sourceScreen()});
    theModal.onClose = function(){
      // the CTA leaves the page to the checkout, so a close without it is a dismissal
      if(!upgradeTapped) segment.track("Premium Screen Dismissed",{source_screen:sourceScreen()});
    }
    function isYearlySelected(){
      return theModal.querySelector('.card.premium').classList.contains('selected');
    }
    function refreshButtonData(){
      var
        yearly = theModal.querySelector('.card.premium').classList.contains('selected'),
        button = theModal.querySelector('a.button'),
        subtitle = button.nextElementSibling.lastElementChild,
        priceBean = yearly?twocheckoutUrls.yearly:twocheckoutUrls.monthly;

      button.setAttribute('href',priceBean.buyUrl)
      if(priceBean.trialDays){
        button.innerHTML = i18n.get('premium_paywall.cta.button_trial',priceBean.trialDays)
        subtitle.innerHTML = i18n.get('premium_paywall.cta.subtitle_trial')
      }else{
        button.innerHTML = i18n.get('actions.continue')
        subtitle.innerHTML = i18n.get('premium_paywall.cta.subtitle')
      }
    }
    refreshButtonData()
    theModal.addEventListener('click',(evt)=>{
      const target = evt.target as HTMLElement;
      const card = target?.closest('.card.premium')
      if (card) {
        theModal.querySelectorAll('.card.premium').forEach(element => {
          if(element===card){
            element.classList.add('selected')
            element.querySelector('icon').innerHTML = Icons.ic_checked.toHTML();
          }else{
            element.classList.remove('selected')
            element.querySelector('icon').innerHTML = Icons.ic_checkbox.toHTML();
          }
        });
        refreshButtonData()
        segment.track("Premium Plan Selected",{plan_type:isYearlySelected()?"yearly":"monthly",source_screen:sourceScreen()});
      }
      if(isKid && target?.closest('a')){
        evt.preventDefault()
        modalBlockedForKid();
        return;
      }
      if(target?.closest('a.button')){
        upgradeTapped = true;
        segment.track("Premium Upgrade Tapped",{plan_type:isYearlySelected()?"yearly":"monthly",source_screen:sourceScreen()});
      }else{
        trackPremiumLinks(target);
      }
    })
    theModal.querySelectorAll('.card.faq>flexrow').forEach((cardHead,questionIndex)=>cardHead.addEventListener('click',(evt)=>{
      const opened = cardHead.parentElement.classList.toggle('open')
      if(opened) segment.track("Premium FAQ Viewed",{question_index:questionIndex});
    }))

    if(twocheckoutUrls.yearlyPromo && !isKid){
      promopopup(data)
    }
    theModal.querySelector('.promolink')?.addEventListener('click',(evt)=>{
      if(isKid){
        modalBlockedForKid();
      }else{
        segment.track("Premium Promo Offer Tapped");
        promopopup(data)
      }
    })

  }
});

function modalBlockedForKid(){
  modal.simple({
    title:"premium.blockedtitle",
    lottie:"images/lottie/anim_teddy_bear.json",
    message:"premium.blockeddescription",
    button:"premium.blockedbutton"
  })
}

function formatPrice(currency :string, price :number){
  const formatteur = new Intl.NumberFormat(i18n.getSelectedLanguage(), {
    style: 'currency',
    currency:currency||'USD'
  });
  return formatteur.format(price)
}

// replaces the {{}} and puts the [[]] in bold
function renderTemplate(str: string, values: Record<string, string>): string {
  // 1. replace the {{variable}} by their value
  let result = str.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => values[key] ?? "");

  // 2. put what is between [[...]] in bold
  result = result.replace(/\[\[\s*(.*?)\s*\]\]/g, "<hl>$1</hl>");

  return result;
}